import React, {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTheme } from "./ThemeContext";

const REMEMBER_KEY = "eva_remember_me";
const ACCESS_TOKEN_KEYS = ["accessToken", "eva:accessToken", "access_token"];
const STORAGE_KEY_SELECTED_ADDRESS = "eva:leftbar:selected-address-global";
const STORAGE_KEY_SELECTED_COMPANY = "eva:home:selected-company";

const API_BASE = "https://api.logvi-havox.com";

function getApiBase(): string {
  return API_BASE;
}

export type MenuItemTipo = "route" | "function";

export type ModulePermissions = {
  canView?: boolean | null;
  canCreate?: boolean | null;
  canUpdate?: boolean | null;
  canDelete?: boolean | null;
  canReport?: boolean | null;
};

export type SubitemMenu = {
  label: string;
  codigo: string;

  tipoItem?: MenuItemTipo;
  actionKey?: string | null;
  routeEnabled?: boolean;

  devOnly?: boolean;
  developed?: boolean;
  migrated?: boolean;
  markOrange?: boolean;
  markBlue?: boolean;
  themeColor?: string | null;
  children?: SubitemMenu[];
};

export type ModuloDepartamento = {
  id?: number | string | null;
  label: string;
  codigo: string;
  themeColor?: string | null;
  devOnly?: boolean;
  developed?: boolean;
  migrated?: boolean;
  markOrange?: boolean;
  markBlue?: boolean;
  permissions?: ModulePermissions | null;
  subitems?: SubitemMenu[];
};

export type DepartamentoAcessivel = {
  id: number | string;
  nome: string;
  slug: string;
  routeKey?: string | null;
  themeColor?: string | null;
  iconKey?: string | null;
  modulos?: ModuloDepartamento[];
};

export type TipoUnidadeEndereco =
  | "matriz"
  | "filial"
  | "base"
  | "escritorio"
  | "escritório"
  | "laboratorio"
  | "laboratório"
  | "operacional"
  | string;

export type EnderecoDisponivel = {
  id: number | string;
  empresa_id?: number | string | null;

  nome?: string | null;

  tipo_unidade?: TipoUnidadeEndereco | null;
  tipoUnidade?: TipoUnidadeEndereco | null;

  is_matriz?: boolean;
  isMatriz?: boolean;

  logradouro?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  uf?: string | null;
  cep?: string | null;
  ativo?: boolean;
  ordem?: number | null;
  label?: string | null;
  criado_em?: string | null;
};

export type EnderecoComModulos = EnderecoDisponivel & {
  departamentos_acessiveis?: DepartamentoAcessivel[];
  modulos_total?: number;
};

export type EmpresaSessao = {
  id?: number | string | null;
  nome?: string | null;
  nome_fantasia?: string | null;
  razao_social?: string | null;
  cnpj?: string | null;
  ativo?: boolean | null;
  area_atuacao?: string | null;
};

export type EmpresaAcessivel = {
  id: number | string;
  nome: string;
  label?: string | null;
  razao_social?: string | null;
  nome_fantasia?: string | null;
  cnpj?: string | null;
  papel?: string | null;
  ambiente_padrao?: boolean | null;
  tipo_ambiente?: string | null;
};

export type Sessao = {
  id: number;
  nome: string;
  email?: string;
  picture?: string | number | null;
  departamento?: string | null;
  funcao?: string | null;
  ativo?: boolean;

  empresa_razao_social?: string | null;
  empresa_nome?: string | null;
  empresa_nome_fantasia?: string | null;
  empresa_cnpj?: string | null;
  empresa_tipo_ambiente?: string | null;

  empresa?: EmpresaSessao | null;

  empresa_id?: string | number | null;
  empresa_ativa_id?: string | number | null;
  empresa_principal_id?: string | number | null;
  empresas_acessiveis?: EmpresaAcessivel[];

  roles?: string[];
  solicitante_id?: string | number | null;
  conta_id?: string | number | null;
  uid?: string | number | null;
  scopes?: string[];

  ttl?: number | null;
  ts?: number | null;
  token_type?: string | null;
  device_id?: string | null;
  is_shared_device?: boolean;

  endereco_selecionado_id?: number | string | null;
  enderecos_disponiveis?: EnderecoDisponivel[];
  enderecos_com_modulos?: EnderecoComModulos[];
  departamentos_acessiveis?: DepartamentoAcessivel[];
};

type AuthContextValue = {
  loading: boolean;
  sessao: Sessao | null;
  error: string | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  trocarEmpresa: (empresaId: number | string) => Promise<void>;
  switchingEmpresa: boolean;
  apiBase: string;
};

type AuthMeResponse = {
  ok?: boolean;
  sessao?: any;
  empresa?: any;
  config?: any;
  departamentos_acessiveis?: any[];
  enderecos_disponiveis?: any[];
  enderecos_com_modulos?: any[];
  endereco_selecionado_id?: number | string | null;

  empresa_id?: number | string | null;
  empresa_ativa_id?: number | string | null;
  empresa_principal_id?: number | string | null;
  empresas_acessiveis?: any[];
} | null;

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function toNullableString(value: any): string | null {
  if (value === undefined || value === null) return null;
  const str = String(value).trim();
  return str.length > 0 ? str : null;
}

function toNullableNumber(value: any): number | null {
  if (value === undefined || value === null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function toBooleanLoose(value: any, fallback = false): boolean {
  if (value === undefined || value === null) return fallback;

  if (typeof value === "boolean") return value;

  if (typeof value === "number") {
    return value === 1;
  }

  const raw = String(value).trim().toLowerCase();

  if (["true", "1", "sim", "s", "yes", "y"].includes(raw)) return true;
  if (["false", "0", "nao", "não", "n", "no"].includes(raw)) return false;

  return fallback;
}

function normalizeTipoUnidade(value: any): TipoUnidadeEndereco | null {
  const raw = toNullableString(value);
  if (!raw) return null;

  const normalized = raw
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (normalized === "matriz") return "matriz";
  if (normalized === "filial") return "filial";
  if (normalized === "base") return "base";
  if (normalized === "escritorio") return "escritorio";
  if (normalized === "laboratorio") return "laboratorio";
  if (normalized === "operacional") return "operacional";

  return raw;
}

export function getEnderecoTipoLabel(
  endereco?: Partial<EnderecoDisponivel> | null
): string {
  if (!endereco) return "Unidade";

  const isMatriz =
    endereco.is_matriz === true ||
    endereco.isMatriz === true ||
    normalizeTipoUnidade(endereco.tipo_unidade ?? endereco.tipoUnidade) ===
      "matriz";

  if (isMatriz) return "Matriz";

  const tipo = normalizeTipoUnidade(
    endereco.tipo_unidade ?? endereco.tipoUnidade
  );

  if (tipo === "filial") return "Filial";
  if (tipo === "base") return "Base";
  if (tipo === "escritorio") return "Escritório";
  if (tipo === "laboratorio") return "Laboratório";
  if (tipo === "operacional") return "Operacional";

  return tipo ? String(tipo).toUpperCase() : "Unidade";
}

export function getEnderecoDisplayLabel(
  endereco?: Partial<EnderecoDisponivel> | null
): string {
  if (!endereco) return "";

  const tipoLabel = getEnderecoTipoLabel(endereco);
  const baseLabel =
    toNullableString(endereco.label) ||
    toNullableString(endereco.nome) ||
    [
      [endereco.logradouro, endereco.numero]
        .map((x) => toNullableString(x))
        .filter(Boolean)
        .join(", "),
      [endereco.bairro, endereco.cidade, endereco.uf]
        .map((x) => toNullableString(x))
        .filter(Boolean)
        .join(" - "),
    ]
      .filter(Boolean)
      .join(" • ") ||
    `Endereço ${endereco.id ?? ""}`.trim();

  return `${tipoLabel} — ${baseLabel}`;
}

function getStoredAccessToken(): string {
  try {
    for (const key of ACCESS_TOKEN_KEYS) {
      const value = localStorage.getItem(key);
      if (value && value.trim()) return value.trim();
    }
  } catch {
    // ignora
  }

  return "";
}

function clearCompanyLocalState() {
  try {
    localStorage.removeItem(STORAGE_KEY_SELECTED_ADDRESS);
  } catch {
    // ignora
  }
}

function mapNullableBoolean(value: any): boolean | null {
  if (value === undefined || value === null) return null;
  return value === true;
}

function mapModulePermissions(mod: any): ModulePermissions | null {
  const source =
    mod?.permissions && typeof mod.permissions === "object"
      ? mod.permissions
      : mod?.permissoes && typeof mod.permissoes === "object"
      ? mod.permissoes
      : null;

  if (!source) return null;

  return {
    canView: mapNullableBoolean(source.canView ?? source.can_view),
    canCreate: mapNullableBoolean(source.canCreate ?? source.can_create),
    canUpdate: mapNullableBoolean(source.canUpdate ?? source.can_update),
    canDelete: mapNullableBoolean(source.canDelete ?? source.can_delete),
    canReport: mapNullableBoolean(source.canReport ?? source.can_report),
  };
}

function mapMenuItemTipo(value: any): MenuItemTipo {
  const raw = String(value || "")
    .trim()
    .toLowerCase();

  return raw === "function" ? "function" : "route";
}

function mapRouteEnabled(value: any, tipoItem: MenuItemTipo): boolean {
  if (value !== undefined && value !== null) {
    return value !== false;
  }

  return tipoItem !== "function";
}

function mapActionKey(sub: any): string | null {
  if (sub?.actionKey !== undefined && sub?.actionKey !== null) {
    const value = String(sub.actionKey).trim();
    return value || null;
  }

  if (sub?.action_key !== undefined && sub?.action_key !== null) {
    const value = String(sub.action_key).trim();
    return value || null;
  }

  return null;
}

function mapSubitemRecursive(sub: any): SubitemMenu {
  const tipoItem = mapMenuItemTipo(sub?.tipoItem ?? sub?.tipo_item);

  const routeEnabled = mapRouteEnabled(
    sub?.routeEnabled ?? sub?.route_enabled,
    tipoItem
  );

  return {
    label: String(sub?.label || ""),
    codigo: String(sub?.codigo || ""),

    tipoItem,
    actionKey: mapActionKey(sub),
    routeEnabled,

    devOnly: sub?.devOnly === true || sub?.dev_only === true,
    developed: sub?.developed === true,
    migrated: sub?.migrated === true,
    markOrange: sub?.markOrange === true || sub?.mark_orange === true,
    markBlue: sub?.markBlue === true || sub?.mark_blue === true,
    themeColor:
      sub?.themeColor !== undefined && sub?.themeColor !== null
        ? String(sub.themeColor)
        : sub?.theme_color !== undefined && sub?.theme_color !== null
        ? String(sub.theme_color)
        : null,
    children: Array.isArray(sub?.children)
      ? sub.children.map((child: any) => mapSubitemRecursive(child))
      : [],
  };
}

function mapModulo(mod: any): ModuloDepartamento {
  return {
    id: mod?.id !== undefined && mod?.id !== null ? mod.id : null,
    label: String(mod?.label || ""),
    codigo: String(mod?.codigo || ""),
    themeColor:
      mod?.themeColor !== undefined && mod?.themeColor !== null
        ? String(mod.themeColor)
        : mod?.theme_color !== undefined && mod?.theme_color !== null
        ? String(mod.theme_color)
        : null,
    devOnly: mod?.devOnly === true || mod?.dev_only === true,
    developed: mod?.developed === true,
    migrated: mod?.migrated === true,
    markOrange: mod?.markOrange === true || mod?.mark_orange === true,
    markBlue: mod?.markBlue === true || mod?.mark_blue === true,
    permissions: mapModulePermissions(mod),
    subitems: Array.isArray(mod?.subitems)
      ? mod.subitems.map((sub: any) => mapSubitemRecursive(sub))
      : [],
  };
}

function mapDepartamento(dep: any): DepartamentoAcessivel {
  return {
    id: dep?.id,
    nome: String(dep?.nome || ""),
    slug: String(dep?.slug || ""),
    routeKey:
      dep?.routeKey !== undefined && dep?.routeKey !== null
        ? String(dep.routeKey)
        : dep?.route_key !== undefined && dep?.route_key !== null
        ? String(dep.route_key)
        : null,
    themeColor:
      dep?.themeColor !== undefined && dep?.themeColor !== null
        ? String(dep.themeColor)
        : dep?.theme_color !== undefined && dep?.theme_color !== null
        ? String(dep.theme_color)
        : null,
    iconKey:
      dep?.iconKey !== undefined && dep?.iconKey !== null
        ? String(dep.iconKey)
        : dep?.icon_key !== undefined && dep?.icon_key !== null
        ? String(dep.icon_key)
        : null,
    modulos: Array.isArray(dep?.modulos)
      ? dep.modulos.map((mod: any) => mapModulo(mod))
      : [],
  };
}

function mapEndereco(end: any): EnderecoDisponivel {
  const tipoUnidade = normalizeTipoUnidade(
    end?.tipo_unidade ??
      end?.tipoUnidade ??
      end?.tipo ??
      end?.tipo_endereco ??
      end?.tipoEndereco
  );

  const isMatriz = toBooleanLoose(
    end?.is_matriz ?? end?.isMatriz ?? end?.matriz,
    tipoUnidade === "matriz"
  );

  return {
    id: end?.id,
    empresa_id:
      end?.empresa_id !== undefined && end?.empresa_id !== null
        ? end.empresa_id
        : null,

    nome: toNullableString(end?.nome),

    tipo_unidade: tipoUnidade,
    tipoUnidade,

    is_matriz: isMatriz,
    isMatriz,

    logradouro: toNullableString(end?.logradouro),
    numero: toNullableString(end?.numero),
    complemento: toNullableString(end?.complemento),
    bairro: toNullableString(end?.bairro),
    cidade: toNullableString(end?.cidade),
    uf: toNullableString(end?.uf),
    cep: toNullableString(end?.cep),
    ativo:
      end?.ativo !== undefined && end?.ativo !== null
        ? end.ativo === true
        : undefined,
    ordem: toNullableNumber(end?.ordem),
    label: toNullableString(end?.label),
    criado_em: toNullableString(end?.criado_em),
  };
}

function mapEnderecoComModulos(end: any): EnderecoComModulos {
  return {
    ...mapEndereco(end),
    departamentos_acessiveis: Array.isArray(end?.departamentos_acessiveis)
      ? end.departamentos_acessiveis.map((dep: any) => mapDepartamento(dep))
      : [],
    modulos_total:
      end?.modulos_total !== undefined && end?.modulos_total !== null
        ? Number(end.modulos_total)
        : 0,
  };
}

function mapEmpresaAcessivel(empresa: any): EmpresaAcessivel | null {
  if (!empresa || empresa.id === undefined || empresa.id === null) {
    return null;
  }

  const nome =
    toNullableString(empresa.nome) ||
    toNullableString(empresa.nome_fantasia) ||
    toNullableString(empresa.razao_social) ||
    `Empresa ${empresa.id}`;

  const tipoAmbiente = toNullableString(
    empresa.tipo_ambiente ?? empresa.tipoAmbiente
  );

  const label =
    toNullableString(empresa.label) ||
    (tipoAmbiente === "desenvolvimento"
      ? `${nome} - Desenvolvimento`
      : tipoAmbiente === "homologacao"
      ? `${nome} - Homologação`
      : `${nome} - Produção`);

  return {
    id: empresa.id,
    nome,
    label,
    razao_social: toNullableString(empresa.razao_social),
    nome_fantasia: toNullableString(empresa.nome_fantasia),
    cnpj: toNullableString(empresa.cnpj),
    papel: toNullableString(empresa.papel),
    ambiente_padrao: empresa.ambiente_padrao === true,
    tipo_ambiente: tipoAmbiente,
  };
}

function mapEmpresasAcessiveis(
  raw: any,
  data?: AuthMeResponse
): EmpresaAcessivel[] {
  const source = Array.isArray(raw?.empresas_acessiveis)
    ? raw.empresas_acessiveis
    : Array.isArray(data?.empresas_acessiveis)
    ? data?.empresas_acessiveis
    : [];

  return source
    .map((empresa: any) => mapEmpresaAcessivel(empresa))
    .filter((empresa: EmpresaAcessivel | null): empresa is EmpresaAcessivel => {
      return empresa !== null;
    });
}

function mapEmpresaSessao(
  data: AuthMeResponse,
  raw: any
): EmpresaSessao | null {
  const empresaApi =
    data?.empresa && typeof data.empresa === "object" ? data.empresa : null;

  if (empresaApi) {
    return {
      id:
        empresaApi.id !== undefined && empresaApi.id !== null
          ? empresaApi.id
          : raw.empresa_id ?? null,
      nome: toNullableString(empresaApi.nome ?? raw.empresa_nome),
      nome_fantasia: toNullableString(
        empresaApi.nome_fantasia ?? raw.empresa_nome_fantasia
      ),
      razao_social: toNullableString(
        empresaApi.razao_social ?? raw.empresa_razao_social
      ),
      cnpj: toNullableString(empresaApi.cnpj ?? raw.empresa_cnpj),
      ativo:
        empresaApi.ativo !== undefined && empresaApi.ativo !== null
          ? empresaApi.ativo === true
          : null,
      area_atuacao: toNullableString(empresaApi.area_atuacao),
    };
  }

  return {
    id:
      raw.empresa_id !== undefined && raw.empresa_id !== null
        ? raw.empresa_id
        : null,
    nome: toNullableString(raw.empresa_nome),
    nome_fantasia: toNullableString(raw.empresa_nome_fantasia),
    razao_social: toNullableString(raw.empresa_razao_social),
    cnpj: toNullableString(raw.empresa_cnpj),
    ativo: null,
    area_atuacao: null,
  };
}

function mapSessao(data: AuthMeResponse): Sessao | null {
  if (!data?.ok || !data?.sessao?.nome) {
    return null;
  }

  const raw = data.sessao;

  const roles: string[] = Array.isArray(raw.roles)
    ? raw.roles.map((x: any) => String(x))
    : [];

  const scopes: string[] = Array.isArray(raw.scopes)
    ? raw.scopes.map((x: any) => String(x))
    : [];

  const departamentosAcessiveis: DepartamentoAcessivel[] = Array.isArray(
    raw.departamentos_acessiveis
  )
    ? raw.departamentos_acessiveis.map((dep: any) => mapDepartamento(dep))
    : Array.isArray(data?.departamentos_acessiveis)
    ? data.departamentos_acessiveis.map((dep: any) => mapDepartamento(dep))
    : [];

  const enderecosDisponiveis: EnderecoDisponivel[] = Array.isArray(
    raw.enderecos_disponiveis
  )
    ? raw.enderecos_disponiveis.map((end: any) => mapEndereco(end))
    : Array.isArray(data?.enderecos_disponiveis)
    ? data.enderecos_disponiveis.map((end: any) => mapEndereco(end))
    : [];

  const enderecosComModulos: EnderecoComModulos[] = Array.isArray(
    raw.enderecos_com_modulos
  )
    ? raw.enderecos_com_modulos.map((end: any) => mapEnderecoComModulos(end))
    : Array.isArray(data?.enderecos_com_modulos)
    ? data.enderecos_com_modulos.map((end: any) => mapEnderecoComModulos(end))
    : [];

  const enderecoSelecionadoId =
    raw.endereco_selecionado_id !== undefined &&
    raw.endereco_selecionado_id !== null
      ? raw.endereco_selecionado_id
      : data?.endereco_selecionado_id !== undefined &&
        data?.endereco_selecionado_id !== null
      ? data.endereco_selecionado_id
      : null;

  const empresaAtivaId =
    raw.empresa_ativa_id !== undefined && raw.empresa_ativa_id !== null
      ? raw.empresa_ativa_id
      : data?.empresa_ativa_id !== undefined && data?.empresa_ativa_id !== null
      ? data.empresa_ativa_id
      : raw.empresa_id !== undefined && raw.empresa_id !== null
      ? raw.empresa_id
      : data?.empresa_id !== undefined && data?.empresa_id !== null
      ? data.empresa_id
      : null;

  const empresaPrincipalId =
    raw.empresa_principal_id !== undefined && raw.empresa_principal_id !== null
      ? raw.empresa_principal_id
      : data?.empresa_principal_id !== undefined &&
        data?.empresa_principal_id !== null
      ? data.empresa_principal_id
      : raw.empresa_id !== undefined && raw.empresa_id !== null
      ? raw.empresa_id
      : null;

  const empresasAcessiveis = mapEmpresasAcessiveis(raw, data);
  const empresaSessao = mapEmpresaSessao(data, raw);

  return {
    id: Number(raw.id ?? raw.uid ?? 0),
    uid: raw.uid ?? null,
    nome: String(raw.nome || ""),
    email: raw.email ?? undefined,
    picture: raw.picture ?? null,
    departamento:
      typeof raw.departamento === "string" ? String(raw.departamento) : null,
    funcao: typeof raw.funcao === "string" ? String(raw.funcao) : null,
    ativo:
      raw.ativo !== undefined && raw.ativo !== null
        ? raw.ativo === true
        : undefined,

    empresa_razao_social: toNullableString(raw.empresa_razao_social),
    empresa_nome: toNullableString(raw.empresa_nome),
    empresa_nome_fantasia: toNullableString(raw.empresa_nome_fantasia),
    empresa_cnpj: toNullableString(raw.empresa_cnpj),
    empresa_tipo_ambiente: toNullableString(raw.empresa_tipo_ambiente),

    empresa: empresaSessao,

    empresa_id: empresaAtivaId,
    empresa_ativa_id: empresaAtivaId,
    empresa_principal_id: empresaPrincipalId,
    empresas_acessiveis: empresasAcessiveis,

    roles,
    scopes,
    solicitante_id:
      raw.solicitante_id !== undefined && raw.solicitante_id !== null
        ? raw.solicitante_id
        : null,
    conta_id:
      raw.conta_id !== undefined && raw.conta_id !== null ? raw.conta_id : null,
    ttl: raw.ttl !== undefined && raw.ttl !== null ? Number(raw.ttl) : null,
    ts: raw.ts !== undefined && raw.ts !== null ? Number(raw.ts) : null,
    token_type:
      raw.token_type !== undefined && raw.token_type !== null
        ? String(raw.token_type)
        : null,
    device_id:
      raw.device_id !== undefined && raw.device_id !== null
        ? String(raw.device_id)
        : null,
    is_shared_device: raw.is_shared_device === true,

    endereco_selecionado_id: enderecoSelecionadoId,
    enderecos_disponiveis: enderecosDisponiveis,
    enderecos_com_modulos: enderecosComModulos,
    departamentos_acessiveis: departamentosAcessiveis,
  };
}

async function fetchAuthMe(apiBase: string): Promise<AuthMeResponse> {
  const r = await fetch(`${apiBase}/auth/me-hml`, {
    method: "GET",
    credentials: "include",
    cache: "no-store",
  });

  if (r.status === 401 || r.status === 403) {
    return null;
  }

  if (!r.ok) {
    throw new Error("Erro ao carregar sessão");
  }

  const data = await r.json().catch(() => null);
  return data;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { setTheme, setSystemTitle } = useTheme();

  const apiBase = useMemo(() => getApiBase(), []);

  const isPublicAuthRoute = useMemo(() => {
    const path = location.pathname;

    return (
      path === "/entrar" ||
      path === "/login" ||
      path === "/cadastro" ||
      path === "/redefinir-senha" ||
      path === "/sessao-encerrada"
    );
  }, [location.pathname]);

  const rememberMe = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(REMEMBER_KEY) || "false") === true;
    } catch {
      return false;
    }
  }, []);

  const hasResolvedOnceRef = useRef(false);
  const switchingEmpresaRef = useRef(false);

  const clearRemember = useCallback(() => {
    if (!rememberMe) {
      try {
        localStorage.removeItem(REMEMBER_KEY);
      } catch {
        // ignora
      }
    }
  }, [rememberMe]);

  const applyConfigFromApi = useCallback(
    (config: any) => {
      if (!config) return;

      if (config.theme) {
        try {
          setTheme(config.theme);
        } catch {
          // ignora
        }
      }

      const stRaw = config.systemTitle;
      let st: string | null = null;

      if (Array.isArray(stRaw)) {
        st = stRaw.join(" • ");
      } else if (typeof stRaw === "string") {
        st = stRaw;
      }

      if (st) {
        setSystemTitle(st);
      }
    },
    [setTheme, setSystemTitle]
  );

  const { data, isLoading, error, refetch, isFetched } = useQuery<
    AuthMeResponse,
    Error
  >({
    queryKey: ["auth", "session", apiBase],
    queryFn: () => fetchAuthMe(apiBase),
    enabled: !isPublicAuthRoute,
    retry: false,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  const sessao = useMemo(() => mapSessao(data ?? null), [data]);

  useEffect(() => {
    if (data?.config) {
      applyConfigFromApi(data.config);
    }
  }, [data, applyConfigFromApi]);

  useEffect(() => {
    if (isFetched) {
      hasResolvedOnceRef.current = true;
    }
  }, [isFetched]);

  useEffect(() => {
    if (isLoading) return;
    if (!hasResolvedOnceRef.current) return;

    const path = location.pathname;
    const isOnLogin = path === "/entrar" || path === "/login";
    const isOnSessaoEncerrada = path === "/sessao-encerrada";

    if (!sessao) {
      clearRemember();

      if (!isOnLogin && !isOnSessaoEncerrada) {
        navigate("/sessao-encerrada", { replace: true });
      }
    }
  }, [sessao, isLoading, clearRemember, location.pathname, navigate]);

  const refresh = useCallback(async () => {
    await refetch({ throwOnError: false });
  }, [refetch]);

  const trocarEmpresa = useCallback(
    async (empresaId: number | string) => {
      const sessaoAtual = sessao;

      if (!sessaoAtual) {
        throw new Error("Sessão não carregada.");
      }

      const empresaIdNumber = Number(empresaId);

      if (!Number.isFinite(empresaIdNumber)) {
        throw new Error("Empresa inválida.");
      }

      const empresaAtual =
        sessaoAtual.empresa_ativa_id !== undefined &&
        sessaoAtual.empresa_ativa_id !== null
          ? Number(sessaoAtual.empresa_ativa_id)
          : sessaoAtual.empresa_id !== undefined &&
            sessaoAtual.empresa_id !== null
          ? Number(sessaoAtual.empresa_id)
          : null;

      if (empresaAtual === empresaIdNumber) {
        return;
      }

      const empresasPermitidas = Array.isArray(sessaoAtual.empresas_acessiveis)
        ? sessaoAtual.empresas_acessiveis
        : [];

      const possuiAcesso = empresasPermitidas.some(
        (empresa) => Number(empresa.id) === empresaIdNumber
      );

      if (!possuiAcesso) {
        throw new Error("Usuário sem acesso ao ambiente solicitado.");
      }

      if (switchingEmpresaRef.current) {
        return;
      }

      switchingEmpresaRef.current = true;

      try {
        const accessToken = getStoredAccessToken();

        const r = await fetch(`${apiBase}/auth/trocar-empresa`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({
            empresa_id: empresaIdNumber,
            empresa_ativa_id: empresaIdNumber,
          }),
        });

        if (r.status === 401 || r.status === 403) {
          throw new Error(
            "Sessão expirada ou sem permissão para trocar ambiente."
          );
        }

        if (!r.ok) {
          throw new Error(`Falha ao trocar ambiente. HTTP ${r.status}`);
        }

        const response = await r.json().catch(() => null);

        if (response?.ok === false) {
          throw new Error(response?.erro || "Falha ao trocar ambiente.");
        }

        try {
          localStorage.setItem(
            STORAGE_KEY_SELECTED_COMPANY,
            JSON.stringify(String(empresaIdNumber))
          );
        } catch {
          // ignora
        }

        clearCompanyLocalState();

        queryClient.setQueryData(["auth", "session", apiBase], response);

        await queryClient.invalidateQueries({
          queryKey: ["auth", "session", apiBase],
        });

        await refetch({ throwOnError: false });
      } finally {
        switchingEmpresaRef.current = false;
      }
    },
    [apiBase, queryClient, refetch, sessao]
  );

  const logout = useCallback(async () => {
    try {
      await fetch(`${apiBase}/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      }).catch(() => null);
    } finally {
      try {
        localStorage.removeItem(REMEMBER_KEY);
        localStorage.removeItem("access_token");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("eva:accessToken");
        localStorage.removeItem(STORAGE_KEY_SELECTED_ADDRESS);
        localStorage.removeItem(STORAGE_KEY_SELECTED_COMPANY);
      } catch {
        // ignora
      }

      queryClient.setQueryData(["auth", "session", apiBase], null);
      queryClient.removeQueries({ queryKey: ["auth", "session", apiBase] });

      window.location.assign("/entrar");
    }
  }, [apiBase, queryClient]);

  const value: AuthContextValue = {
    loading: !isPublicAuthRoute && isLoading && !hasResolvedOnceRef.current,
    sessao,
    error: error?.message || null,
    refresh,
    logout,
    trocarEmpresa,
    switchingEmpresa: switchingEmpresaRef.current,
    apiBase,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  const ctx = useContext(AuthContext);

  if (!ctx) {
    throw new Error("useAuth deve ser usado dentro de <AuthProvider>");
  }

  return ctx;
}
